import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import { toPng } from "html-to-image";
import ScreenshotLangPicker from "../../components/ScreenshotLangPicker";
import { appendScreenshotLang, useScreenshotTranslation } from "../../lib/screenshotI18n";
import { WORD_WHEEL_QUEST_TITLE, screenshotPath } from "../../prototype/mobile-word-wheel/routes";
import {
    isScreenshotSceneConfigured,
    WORD_WHEEL_SCREENSHOT_SCENES,
} from "../../prototype/mobile-word-wheel/screenshotScenes";
import { wordWheelShellBgForScene } from "../../prototype/mobile-word-wheel/styles/screenshotStyles";
import { downloadPngDataUrl } from "./downloadPngDataUrl";
import {
    persistGoogleAdFormatTab,
    readGoogleAdFormatTab,
} from "./googleAdFormatTabStorage";

/** Google Ads image sizes. */
const AD_FORMATS = [
    { id: "landscape", label: "Landscape", width: 1200, height: 628 },
    { id: "square", label: "Square", width: 1200, height: 1200 },
    { id: "portrait", label: "Portrait", width: 1080, height: 1920 },
];

const FORMAT_TAB_STORAGE_KEY = "wordWheelGoogleAd.formatTab";

/** Match phone capture window used for Word Wheel screenshots. */
const PHONE_W = 430;
const PHONE_H = 940;
/** Preview max width — landscape is larger so creatives stay readable. */
const PREVIEW_MAX_W = 180;
const PREVIEW_MAX_W_LANDSCAPE = Math.round(PREVIEW_MAX_W * 2 * 1.5);

function screenshotPageHref(path, lang, format) {
    if (typeof window === "undefined") return path;
    const withLang = appendScreenshotLang(path, lang);
    const url = new URL(withLang, window.location.origin);
    url.searchParams.set("googleAdW", String(format.width));
    url.searchParams.set("googleAdH", String(format.height));
    if (format.id === "landscape") {
        url.searchParams.set("googleAdLandscape", "1");
    }
    if (format.id === "square") {
        url.searchParams.set("googleAdSquare", "1");
    }
    return url.toString();
}

async function loadImage(src) {
    const img = new Image();
    await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = () => reject(new Error("Failed to decode capture"));
        img.src = src;
    });
    return img;
}

function phoneDocumentFromAdIframe(iframe) {
    const doc = iframe?.contentDocument;
    if (!doc) return null;
    const nested = doc.querySelector("iframe[data-ww-ad-phone='1']");
    return nested?.contentDocument || doc;
}

async function capturePhonePng(iframe) {
    const phoneDoc = phoneDocumentFromAdIframe(iframe);
    if (!phoneDoc?.documentElement) {
        throw new Error("Preview still loading — try again in a moment");
    }
    return toPng(phoneDoc.documentElement, {
        cacheBust: true,
        pixelRatio: 1,
        skipFonts: true,
        width: PHONE_W,
        height: PHONE_H,
        backgroundColor: null,
    });
}

/** Paint a CSS background (e.g. linear-gradient / cover image) onto a canvas. */
async function fillWithCssBackground(ctx, width, height, background) {
    const urlMatch = /url\("([^"]+)"\)/.exec(background);
    if (urlMatch?.[1]) {
        await loadImage(urlMatch[1]);
    }
    const mount = document.createElement("div");
    mount.setAttribute("aria-hidden", "true");
    mount.style.cssText = [
        "position:fixed",
        "left:-10000px",
        "top:0",
        `width:${width}px`,
        `height:${height}px`,
        `background:${background}`,
        "pointer-events:none",
    ].join(";");
    document.body.appendChild(mount);
    try {
        const url = await toPng(mount, {
            cacheBust: true,
            pixelRatio: 1,
            skipFonts: true,
            width,
            height,
        });
        const img = await loadImage(url);
        ctx.drawImage(img, 0, 0, width, height);
    } finally {
        document.body.removeChild(mount);
    }
}

/** Square / portrait — shell bleed + contain-fitted phone. */
async function buildLetterboxAdPngDataUrl(iframe, width, height, shellBg) {
    const srcDataUrl = await capturePhonePng(iframe);
    const img = await loadImage(srcDataUrl);
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas unavailable");

    await fillWithCssBackground(ctx, width, height, shellBg);

    const scale = Math.min(width / img.width, height / img.height);
    const drawW = img.width * scale;
    const drawH = img.height * scale;
    ctx.drawImage(img, (width - drawW) / 2, (height - drawH) / 2, drawW, drawH);
    return canvas.toDataURL("image/png");
}

/** Landscape / square — capture the dedicated creative frame. */
async function buildShowcaseAdPngDataUrl(iframe, width, height) {
    const doc = iframe?.contentDocument;
    if (!doc?.documentElement) {
        throw new Error("Preview still loading — try again in a moment");
    }
    const frame =
        doc.querySelector('[data-google-ad-square="1"]') ||
        doc.querySelector('[data-google-ad-landscape="1"]') ||
        doc.documentElement;
    return toPng(frame, {
        cacheBust: true,
        pixelRatio: 1,
        skipFonts: true,
        width,
        height,
    });
}

function AdScreenshotTile({ format, scene, lang }) {
    const iframeRef = useRef(null);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const [iframeReady, setIframeReady] = useState(false);

    const isLandscape = format.id === "landscape";
    const isSquare = format.id === "square";
    const isShowcase = isLandscape || isSquare;
    const href = screenshotPageHref(screenshotPath(scene.slug), lang, format);
    const shellBg = wordWheelShellBgForScene(scene);
    const previewMaxW =
        isLandscape || isSquare ? PREVIEW_MAX_W_LANDSCAPE : PREVIEW_MAX_W;
    const previewScale = Math.min(1, previewMaxW / format.width);
    const previewW = Math.round(format.width * previewScale);
    const previewH = Math.round(format.height * previewScale);

    useEffect(() => {
        setIframeReady(false);
    }, [href, format.id]);

    const handleDownload = useCallback(async () => {
        if (saving) return;
        setSaving(true);
        setSaveError(null);
        try {
            const dataUrl = isShowcase
                ? await buildShowcaseAdPngDataUrl(
                      iframeRef.current,
                      format.width,
                      format.height,
                  )
                : await buildLetterboxAdPngDataUrl(
                      iframeRef.current,
                      format.width,
                      format.height,
                      shellBg,
                  );
            await downloadPngDataUrl(
                dataUrl,
                `word-wheel-quest-google-ad-${scene.slug}-${format.id}-${format.width}x${format.height}.png`,
            );
        } catch (e) {
            setSaveError(e?.message || "Failed to download PNG");
        } finally {
            setSaving(false);
        }
    }, [format, scene.slug, saving, shellBg, isShowcase]);

    return (
        <Box
            sx={{
                flex: `0 0 ${previewW}px`,
                width: previewW,
                mb: 1.5,
            }}
        >
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.25, fontSize: 12 }}>
                Screenshot {scene.slug}
            </Typography>
            <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                    display: "block",
                    mb: 0.75,
                    minHeight: 28,
                    fontSize: 11,
                    lineHeight: 1.25,
                }}
            >
                {scene.label}
            </Typography>
            <Box
                sx={{
                    width: previewW,
                    height: previewH,
                    position: "relative",
                    overflow: "hidden",
                    borderRadius: 1,
                    border: 1,
                    borderColor: "divider",
                    boxShadow: 1,
                    background: isShowcase ? "#0f172a" : shellBg,
                }}
            >
                <Box
                    sx={{
                        width: format.width,
                        height: format.height,
                        transform: `scale(${previewScale})`,
                        transformOrigin: "top left",
                        overflow: "hidden",
                        position: "relative",
                        background: isShowcase ? "#0f172a" : shellBg,
                    }}
                >
                    <Box
                        component="iframe"
                        ref={iframeRef}
                        src={href}
                        title={`Screenshot ${scene.slug} · ${format.label}`}
                        loading="lazy"
                        onLoad={() => {
                            window.setTimeout(() => setIframeReady(true), isShowcase ? 900 : 700);
                        }}
                        sx={{
                            position: "absolute",
                            left: 0,
                            top: 0,
                            width: format.width,
                            height: format.height,
                            border: 0,
                            pointerEvents: "none",
                            display: "block",
                            background: isShowcase ? "#0f172a" : shellBg,
                        }}
                    />
                </Box>
            </Box>
            <Stack spacing={0.5} sx={{ mt: 0.75 }}>
                <Button
                    size="small"
                    variant="contained"
                    startIcon={
                        saving ? <CircularProgress size={14} color="inherit" /> : <SaveAltIcon />
                    }
                    onClick={handleDownload}
                    disabled={saving || !iframeReady}
                    sx={{ minHeight: 36, textTransform: "none", fontWeight: 700 }}
                >
                    {saving ? "Saving…" : "Download"}
                </Button>
                {saveError ? (
                    <Typography variant="caption" color="error">
                        {saveError}
                    </Typography>
                ) : null}
            </Stack>
        </Box>
    );
}

/**
 * Admin page — Google Ads creatives from all Word Wheel screenshots (01–08)
 * in Landscape / Square / Portrait, with per-shot download.
 */
export default function WordWheelGoogleAdPage() {
    const { lang } = useScreenshotTranslation();
    const [formatTab, setFormatTab] = useState(() =>
        readGoogleAdFormatTab(AD_FORMATS, FORMAT_TAB_STORAGE_KEY),
    );
    const scenes = useMemo(
        () => WORD_WHEEL_SCREENSHOT_SCENES.filter(isScreenshotSceneConfigured),
        [],
    );
    const activeFormat = AD_FORMATS[formatTab] ?? AD_FORMATS[0];

    useEffect(() => {
        persistGoogleAdFormatTab(AD_FORMATS, FORMAT_TAB_STORAGE_KEY, formatTab);
    }, [formatTab]);

    return (
        <Box sx={{ p: 2, pb: 6, maxWidth: 1400, mx: "auto" }}>
            <Typography variant="h5" component="h1" sx={{ fontWeight: 700, mb: 0.5 }}>
                {WORD_WHEEL_QUEST_TITLE} — Google Ads
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                All screenshots (01–{String(scenes.length).padStart(2, "0")}) · three Google Ads
                sizes · download PNG under each preview
            </Typography>
            <ScreenshotLangPicker sx={{ mb: 2 }} />

            <Tabs
                value={formatTab}
                onChange={(_, next) => setFormatTab(next)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                    mb: 2,
                    borderBottom: 1,
                    borderColor: "divider",
                    minHeight: 48,
                }}
            >
                {AD_FORMATS.map((format, index) => (
                    <Tab
                        key={format.id}
                        label={`${format.label} · ${format.width}×${format.height}`}
                        id={`ww-google-ad-tab-${index}`}
                        aria-controls={`ww-google-ad-tabpanel-${index}`}
                        sx={{ minHeight: 48, textTransform: "none", fontWeight: 600 }}
                    />
                ))}
            </Tabs>

            <Box
                role="tabpanel"
                id={`ww-google-ad-tabpanel-${formatTab}`}
                aria-labelledby={`ww-google-ad-tab-${formatTab}`}
            >
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {activeFormat.id === "landscape" || activeFormat.id === "square"
                        ? `Dedicated ${activeFormat.label.toLowerCase()} creatives (${activeFormat.width}×${activeFormat.height}) · dual-device / showcase layouts`
                        : `Shell background fills ${activeFormat.label.toLowerCase()} (${activeFormat.width}×${activeFormat.height}) · portrait UI kept in ratio`}
                </Typography>
                <Box
                    sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 1.5,
                        alignItems: "flex-start",
                    }}
                >
                    {scenes.map((scene) => (
                        <AdScreenshotTile
                            key={`${activeFormat.id}-${scene.slug}`}
                            format={activeFormat}
                            scene={scene}
                            lang={lang}
                        />
                    ))}
                </Box>
            </Box>
        </Box>
    );
}
